import { RegressionTestingStrategy, ReleaseStepType } from '../types';
import * as React from 'react';
import { flatMap } from 'lodash';
import Form, { useForm, useWatch } from 'antd/lib/form/Form';
import Modal from 'antd/lib/modal';
import {
    descriptionForRegressionTestingStrategy,
    getRegressionTestingStepFields,
    transformStepConfigSettingsToUpdateRequestValues,
} from '@/components/stepConfigs/stepConfigSettingsForms';
import { RegressionTestingStepSettings } from '@/types/stepConfig';
import { renderFormFields } from '@/helpers/formHelpers';
import { Divider } from 'antd/lib';
import Text from 'antd/lib/typography/Text';

interface RegressionTestingSettingsProps {
    open: boolean;
    isLoading: boolean;
    currentRegressionStrategy: RegressionTestingStrategy;
    onChangeStrategy: (strategy: RegressionTestingStrategy) => void;
    onCancel: () => void;
}

const RegressionTestingSettings = (props: RegressionTestingSettingsProps) => {
    const [formIsValid, setFormIsValid] = React.useState(false);
    const [form] = useForm();
    const selectedStrategy = useWatch<RegressionTestingStrategy>('regressionTestingStrategy', form);

    const onOk = () => {
        form.validateFields()
            .then((values) => {
                const stepSettings = transformStepConfigSettingsToUpdateRequestValues(
                    ReleaseStepType.RegressionTesting,
                    values,
                    null,
                    null,
                    null,
                );
                props.onChangeStrategy(stepSettings.regressionTestingStrategy);
            })
            .catch(() => setFormIsValid(false));
    };

    const onFieldsChange = () => {
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));

        setFormIsValid(errors.length === 0);
    };

    const initialValues: RegressionTestingStepSettings = {
        regressionTestingStrategy: props.currentRegressionStrategy ?? RegressionTestingStrategy.ManualUpdatesOnly,
    };

    const stepConfigSettingsFields = getRegressionTestingStepFields();
    const description = descriptionForRegressionTestingStrategy(selectedStrategy);

    return (
        <Modal
            title="Build testing status settings"
            open={props.open}
            confirmLoading={props.isLoading}
            onOk={onOk}
            okButtonProps={{ disabled: !formIsValid, loading: props.isLoading }}
            onCancel={props.onCancel}
            width={700}
            destroyOnClose
        >
            <div>
                <Form
                    form={form}
                    name="regression"
                    initialValues={initialValues}
                    onFieldsChange={onFieldsChange}
                    validateMessages={{
                        required: '${label} is required.',
                    }}
                    style={{ marginBottom: 0 }}
                    preserve={false}
                >
                    {renderFormFields(stepConfigSettingsFields)}
                    {description && (
                        <Text type="secondary" style={{ marginTop: 20, marginBottom: 0 }}>
                            {description}
                        </Text>
                    )}
                </Form>

                <div style={{ marginTop: description ? 20 : 30 }}>
                    <Divider />
                    <Text type="secondary">
                        When a CI build is locked, build testing status preferences are ignored and the locked build is
                        used for regression testing.
                    </Text>
                </div>
            </div>
        </Modal>
    );
};

export default RegressionTestingSettings;
